import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pill } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import type { FieldErrors, SubmitHandler } from "react-hook-form";

import { useInsulins, usePatient } from "@/hooks";
import { useMultiStepFormStore } from "@/stores";
import { medicalInformationFormSchema } from "@/types";
import type {
  InsulinFormType,
  MedicalInformationFormType,
  NoInsulinFormType,
} from "@/types";
import {
  ComboboxWithLabels,
  ControlledRadioGroupCard,
  FormField,
} from "@/ui/common";
import { NotQualifyModal } from "@/ui/common/NotQualifyModal";
import {
  PATIENT_DISQUALIFY_REASONS,
  YES_NO_OPTIONS,
  YES_NO_RADIO_OPTIONS,
} from "@/utils";
import FormButtons from "../FormButtons";

const hasInsulinForm = (
  data: string,
  _errors: FieldErrors<MedicalInformationFormType>,
): _errors is FieldErrors<InsulinFormType> => {
  return data === YES_NO_OPTIONS.YES;
};
const noInsulinForm = (
  data: string,
  _errors: FieldErrors<MedicalInformationFormType>,
): _errors is FieldErrors<NoInsulinFormType> => {
  return data === YES_NO_OPTIONS.NO;
};

export function MedicalInformationForm() {
  const [isNotQualifyModalOpen, setIsNotQualifyModalOpen] = useState(false);
  const { setMultiStepFormData, goToNextFormStep, multiStepFormData } =
    useMultiStepFormStore();

  const { useInsulinList } = useInsulins();
  const { data: insulinList } = useInsulinList();

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<MedicalInformationFormType>({
    defaultValues: { isTakingInsulin: "yes" },
    resolver: zodResolver(medicalInformationFormSchema),
  });

  const isTakingInsulin = watch("isTakingInsulin");
  const hasLowBloodSugar = watch("hasLowBloodSugar");

  const showDoesNotQualify =
    isTakingInsulin === YES_NO_OPTIONS.NO &&
    hasLowBloodSugar === YES_NO_OPTIONS.NO;

  const onSubmit: SubmitHandler<MedicalInformationFormType> = (data) => {
    if (showDoesNotQualify) {
      return setIsNotQualifyModalOpen(true);
    }
    if (data.isTakingInsulin === YES_NO_OPTIONS.YES) {
      const insulinLabels = data.insulinNames.map((id) => {
        const found = insulinList?.find((option) => option.id === id);
        return found?.label ?? id;
      });
      setMultiStepFormData({
        medicalInformationFormData: {
          ...data,
          insulinNames: insulinLabels,
        },
      });
    } else {
      setMultiStepFormData({
        medicalInformationFormData: data,
      });
    }

    goToNextFormStep();
  };

  useEffect(() => {
    if (multiStepFormData?.medicalInformationFormData) {
      const { medicalInformationFormData } = multiStepFormData;

      if (medicalInformationFormData.isTakingInsulin === YES_NO_OPTIONS.YES) {
        reset({
          isTakingInsulin: YES_NO_OPTIONS.YES,
          insulinNames: medicalInformationFormData.insulinNames,
        });
      } else {
        reset({
          isTakingInsulin: YES_NO_OPTIONS.NO,
          hasLowBloodSugar: medicalInformationFormData.hasLowBloodSugar,
        });
      }
    }
  }, [multiStepFormData, reset]);

  const { useDisqualifyPatient } = usePatient();

  const { mutate: disqualifyPatient, isPending } = useDisqualifyPatient();

  const handleEndIntake = () => {
    if (multiStepFormData?.generalInformationFormData) {
      const { generalInformationFormData } = multiStepFormData;
      const { firstName, lastName, dateOfBirth, email } =
        generalInformationFormData;
      disqualifyPatient({
        firstName,
        lastName,
        dateOfBirth,
        email,
        reasonToDisqualify: PATIENT_DISQUALIFY_REASONS.NOT_INSULIN,
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <Pill />
          <p className="font-extrabold">Medical information</p>
        </div>
        <p className="text-sm font-extrabold text-neutral-500">
          All fields are required
        </p>
      </div>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid min-h-28 grid-cols-2 gap-x-12 gap-y-10"
      >
        <FormField
          label={"Are you injecting insulin?"}
          error={errors.isTakingInsulin?.message}
          hasLabelSpacing={isTakingInsulin === "no"}
          className="min-h-28 justify-between"
        >
          <ControlledRadioGroupCard
            name="isTakingInsulin"
            control={control}
            options={YES_NO_RADIO_OPTIONS}
          />
        </FormField>
        {hasInsulinForm(isTakingInsulin, errors) && (
          <FormField
            label="What is the name of the insulin you are taking?"
            error={errors.insulinNames?.message}
            className="min-h-28 justify-between"
          >
            <Controller
              control={control}
              name="insulinNames"
              render={({ field, fieldState }) => (
                <ComboboxWithLabels
                  options={insulinList ?? []}
                  value={field.value || []}
                  onChange={field.onChange}
                  hasError={!!fieldState.error?.message}
                  placeholder="Select or search by commercial name"
                  hasMultipleSelection={true}
                />
              )}
            />
          </FormField>
        )}
        {noInsulinForm(isTakingInsulin, errors) && (
          <FormField
            label="Have you had two blood sugar readings lower than 54 in the past 6 months?"
            error={errors.hasLowBloodSugar?.message}
            className="min-h-28 justify-between"
            hasLabelSpacing={isTakingInsulin === "no"}
          >
            <ControlledRadioGroupCard
              name="hasLowBloodSugar"
              control={control}
              options={YES_NO_RADIO_OPTIONS}
            />
          </FormField>
        )}
        <FormButtons />
      </form>
      <NotQualifyModal
        show={isNotQualifyModalOpen}
        title="Patient does not qualify"
        description="The patient is not taking insulin and has not experienced two blood sugar readings below 54 in the past 6 months."
        onClose={() => setIsNotQualifyModalOpen(false)}
        isLoading={isPending}
        handleEndIntake={handleEndIntake}
      />
    </div>
  );
}
